/**
 * @license Codistica
 *
 * Copyright (c) 2020, Codistica and its affiliates.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE.md file in the root directory of this source tree.
 *
 * BUILD DATE: Fri Jul 10 2020 16:51:08 GMT+0200 (Central European Summer Time)
 */
import _newArrowCheck from "@babel/runtime-corejs3/helpers/esm/newArrowCheck";
import _extends from "@babel/runtime-corejs3/helpers/esm/extends";
import _objectWithoutProperties from "@babel/runtime-corejs3/helpers/esm/objectWithoutProperties";
import _typeof from "@babel/runtime-corejs3/helpers/esm/typeof";
import _bindInstanceProperty from "@babel/runtime-corejs3/core-js-stable/instance/bind";
import _classCallCheck from "@babel/runtime-corejs3/helpers/esm/classCallCheck";
import _createClass from "@babel/runtime-corejs3/helpers/esm/createClass";
import _assertThisInitialized from "@babel/runtime-corejs3/helpers/esm/assertThisInitialized";
import _inherits from "@babel/runtime-corejs3/helpers/esm/inherits";
import _createSuper from "@babel/runtime-corejs3/helpers/esm/createSuper";
import _defineProperty from "@babel/runtime-corejs3/helpers/esm/defineProperty";

/** @module react/hocs/with-on-click-outside */
import { eventListenerObjectSupport } from '@codistica/browser';
import React from 'react';
var eventOptions = eventListenerObjectSupport.capture ? {
  capture: true
} : true;

/**
 * @typedef withOnClickOutsidePropsType
 * @property {Function} [onClickOutside=null] - Callback for clickOutside event.
 * @property {*} [children=null] - React prop.
 * @property {*} [forwardedRef=null] - React prop.
 */

/**
 * @description Creates a higher order component with clickOutside event support.
 * @param {(Object<string,*>|string)} Component - React component.
 * @returns {Object<string,*>} Created higher order component.
 */
function withOnClickOutside(Component) {
  var _this2 = this,
      _context4;

  /**
   * @classdesc Higher order component.
   */
  var HOC = /*#__PURE__*/function (_React$Component) {
    _inherits(HOC, _React$Component);

    var _super = _createSuper(HOC);

    /**
     * @description Constructor.
     * @param {withOnClickOutsidePropsType} [props] - Component props.
     */
    function HOC(props) {
      var _context, _context2, _context3;

      var _this;

      _classCallCheck(this, HOC);

      _this = _super.call(this, props);

      _defineProperty(_assertThisInitialized(_this), "componentRef", void 0);

      _defineProperty(_assertThisInitialized(_this), "isOutside", void 0);

      _this.componentRef = null;
      _this.isOutside = false; // BIND METHODS

      _assertThisInitialized(_this).onStart = _bindInstanceProperty(_context = _this.onStart).call(_context, _assertThisInitialized(_this));
      _assertThisInitialized(_this).onEnd = _bindInstanceProperty(_context2 = _this.onEnd).call(_context2, _assertThisInitialized(_this));
      _assertThisInitialized(_this).setComponentRef = _bindInstanceProperty(_context3 = _this.setComponentRef).call(_context3, _assertThisInitialized(_this));
      return _this;
    }
    /**
     * @instance
     * @description React lifecycle.
     * @returns {void} Void.
     */


    _createClass(HOC, [{
      key: "componentDidMount",
      value: function componentDidMount() {
        window.addEventListener('touchstart', this.onStart, eventOptions);
        window.addEventListener('mousedown', this.onStart, eventOptions);
        window.addEventListener('touchend', this.onEnd, eventOptions);
        window.addEventListener('mouseup', this.onEnd, eventOptions);
      }
      /**
       * @instance
       * @description React lifecycle.
       * @returns {void} Void.
       */

    }, {
      key: "componentWillUnmount",
      value: function componentWillUnmount() {
        window.removeEventListener('touchstart', this.onStart, eventOptions);
        window.removeEventListener('mousedown', this.onStart, eventOptions);
        window.removeEventListener('touchend', this.onEnd, eventOptions);
        window.removeEventListener('mouseup', this.onEnd, eventOptions);
      }
      /**
       * @instance
       * @description Callback for start event.
       * @param {Object<string,*>} e - Triggering event.
       * @returns {void} Void.
       */

    }, {
      key: "onStart",
      value: function onStart(e) {
        if (e.type === 'touchstart') {
          if (e.touches.length !== 1) {
            return;
          }
        }

        this.isOutside = this.componentRef && !this.componentRef.contains(e.target);
      }
      /**
       * @instance
       * @description Callback for end event.
       * @param {Object<string,*>} e - Triggering event.
       * @returns {void} Void.
       */

    }, {
      key: "onEnd",
      value: function onEnd(e) {
        var target = null;

        if (e.type === 'touchend') {
          if (e.touches.length === 1) {
            this.isOutside = false;
            return;
          }

          target = document.elementFromPoint(e.changedTouches[0].clientX, e.changedTouches[0].clientY);
        } else {
          target = e.target;
        }

        if (this.isOutside && this.componentRef && !this.componentRef.contains(target)) {
          this.props.onClickOutside && this.props.onClickOutside(e);
        }

        this.isOutside = false;
      }
      /**
       * @instance
       * @description Save and pass component reference.
       * @param {Object<string,*>} ref - Component reference.
       * @returns {void} Void.
       */

    }, {
      key: "setComponentRef",
      value: function setComponentRef(ref) {
        // FORWARD REF
        var forwardedRef = this.props.forwardedRef;

        if (typeof forwardedRef === 'function') {
          forwardedRef(ref);
        } else if (_typeof(forwardedRef) === 'object' && forwardedRef !== null && typeof forwardedRef.current !== 'undefined') {
          forwardedRef.current = ref;
        } // SAVE REF


        this.componentRef = ref;
      }
      /**
       * @instance
       * @description React render method.
       * @returns {Object<string,*>} React component.
       */

    }, {
      key: "render",
      value: function render() {
        var _this$props = this.props,
            onClickOutside = _this$props.onClickOutside,
            children = _this$props.children,
            forwardedRef = _this$props.forwardedRef,
            other = _objectWithoutProperties(_this$props, ["onClickOutside", "children", "forwardedRef"]);

        return /*#__PURE__*/React.createElement(Component, _extends({}, other, {
          ref: this.setComponentRef
        }), children);
      }
    }]);

    return HOC;
  }(React.Component);

  _defineProperty(HOC, "defaultProps", {
    onClickOutside: null,
    children: null,
    forwardedRef: null
  });

  return React.forwardRef(_bindInstanceProperty(_context4 = function _context4(props, ref) {
    _newArrowCheck(this, _this2);

    return /*#__PURE__*/React.createElement(HOC, _extends({}, props, {
      forwardedRef: ref
    }));
  }).call(_context4, this));
}

export { withOnClickOutside };